import { createSlice } from "@reduxjs/toolkit";
import { customersApi } from "../../api/customersApi";
import { toast } from "../../components/common/toast";
import type { AppDispatch, RootState } from "../../app/store";
import type { Customer } from "../../types/customers.types";
import { v4 as uuidv4 } from "uuid";
import i18n from "../../i18n";
import { getCurrentUser } from "../../auth/auth.service";
import {
  uploadMediaFiles,
  createResolvedMedia,
  buildRenamePayload,
  applyRename,
  buildDeletePayload,
  applyDelete,
} from "../../utils/mediaUtils";
import axios from "axios";
import { mediaLinkKey, resolveMediaLinks } from "../../utils/mediaLinkBatch";

export const fetchCustomers = (payload: any = {}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().customers;
    const userData = getCurrentUser();
    const incomingPageIndex = payload.hasOwnProperty("pageIndex")
      ? payload.pageIndex
      : state.pageIndex;

    if (state.isLoading || (!state.hasMore && incomingPageIndex !== 0)) return;

    try {
      dispatch(setLoading(true));

      const requestBody = {
        pageSize: 10,
        pageIndex: incomingPageIndex,
        organisationIds: [`${(await userData).companyId}`],
        sorDecending: payload.hasOwnProperty("sortDecending")
          ? payload.sortDecending
          : true,
        // "" falls through to the backend default, which is now
        // most-recently-updated. Callers can still pass their own.
        orderBy: payload.orderBy || "",
        ids: payload.ids || [],
        searchText: payload.searchText || "",
        resolvePic: payload.hasOwnProperty("resolvePic")
          ? payload.resolvePic
          : true,
        cities: payload.cities || [],
        states: payload.states || [],
        pincodes: payload.pincodes || [],
        roles: payload.roles || [],
        isActive: payload.hasOwnProperty("isActive") ? payload.isActive : null,

        email: payload.email || "",
        mobile: payload.mobile || "",
        locality: payload.locality || "",
      };

      const response = await customersApi.searchCustomers(requestBody);
      const fetchedData = response.data || [];
      const totalCount = response.count || 0;

      dispatch(setTotalCustomers(totalCount));

      // One media request per type for the page, not one or two per customer.
      const PROFILE_MEDIA_TYPE = 1;
      const ATTACHMENT_MEDIA_TYPE = 3;
      const links = await resolveMediaLinks(
        fetchedData as Customer[],
        (c, want) => {
          if (c.profilePic && !c.profilePic.startsWith("http")) {
            want(c.groupId, PROFILE_MEDIA_TYPE, c.profilePic);
          }
          for (const media of (c.medias || []) as any[]) {
            want(c.groupId, ATTACHMENT_MEDIA_TYPE, media.s3Url);
          }
        },
        customersApi.getMediaLink,
        "Customer",
      );

      const resolvedData = fetchedData.map((customer: Customer) => {
        const updatedCustomer = { ...customer };

        if (customer.profilePic) {
          updatedCustomer.profilePicUrl = customer.profilePic.startsWith("http")
            ? customer.profilePic
            : links.get(mediaLinkKey(PROFILE_MEDIA_TYPE, customer.profilePic)) || "";
        }

        if (customer.medias && customer.medias.length > 0) {
          updatedCustomer.medias = customer.medias.map((media: any) => ({
            ...media,
            url: links.get(mediaLinkKey(ATTACHMENT_MEDIA_TYPE, media.s3Url)) || "",
          }));
          updatedCustomer.mediaLinks = null;
        }

        return updatedCustomer;
      });

      const currentLoadedCount =
        incomingPageIndex === 0
          ? resolvedData.length
          : state.items.length + resolvedData.length;

      if (currentLoadedCount >= totalCount || resolvedData.length < 10) {
        dispatch(setHasMore(false));
      } else {
        dispatch(dispatch(setHasMore(true)));
      }

      if (incomingPageIndex === 0) {
        dispatch(setCustomers(resolvedData));
        dispatch(setPageIndex(1));
      } else {
        dispatch(appendCustomers(resolvedData));
        dispatch(setPageIndex(incomingPageIndex + 1));
      }
    } catch (error) {
      toast.error(
        i18n.t("customers:sliceMessages.fetchError", {
          defaultValue: "Failed to fetch customers",
        }),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const uploadCustomerProfilePic = async (file: File): Promise<string> => {
  const fileId = uuidv4();
  const initRes = await customersApi.initUpload([fileId]);

  const uploadUrl = initRes.links[0].t;

  await axios.put(uploadUrl, file, {
    headers: { "Content-Type": file.type },
  });

  return fileId;
};

export const addNewCustomer = (formData: any) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const res = await customersApi.createCustomer({
        ...formData,
        roleId: "94ebaf14-392c-11eb-9b5c-00ffba546a07",
      });

      const newCustomer = {
        ...formData,
        id: res,
        isActive: true,
      };
      const { items } = getState().customers;

      dispatch(setCustomers([newCustomer, ...items]));

      toast.success(
        i18n.t("customers:sliceMessages.addSuccess", {
          defaultValue: "Customer added successfully",
        }),
      );
    } catch (err) {
      toast.error(
        i18n.t("customers:sliceMessages.addError", {
          defaultValue: "Failed to add customer",
        }),
      );
      console.error("Error", err);
    }
  };
};

export const editCustomer = (formData: any) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const apiPayload = { ...formData };

      delete apiPayload.profilePicUrl;
      await customersApi.updateCustomer({
        ...apiPayload,
        roleId: "94ebaf14-392c-11eb-9b5c-00ffba546a07",
      });
      const { items } = getState().customers;

      const updatedList = items.map((item: Customer) =>
        item.id === formData.id ? { ...item, ...formData } : item,
      );

      dispatch(setCustomers(updatedList));

      toast.success(
        i18n.t("customers:sliceMessages.editSuccess", {
          defaultValue: "Customer updated successfully",
        }),
      );
    } catch (err) {
      toast.error(
        i18n.t("customers:sliceMessages.editError", {
          defaultValue: "Failed to update customer",
        }),
      );
      console.error("error", err);
    }
  };
};

export const removeCustomer = (id: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await customersApi.removeCustomer(id);

      const { items } = getState().customers;

      const filteredList = items.filter((item: Customer) => item.id !== id);

      dispatch(setCustomers(filteredList));
      toast.success(
        i18n.t("customers:sliceMessages.removeSuccess", {
          defaultValue: "Customer deleted",
        }),
      );
    } catch (err) {
      toast.error(
        i18n.t("customers:sliceMessages.removeError", {
          defaultValue: "Failed to delete customer",
        }),
      );
    }
  };
};

export const handleActivate = (id: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await customersApi.activateCustomer(id);

      const { items } = getState().customers;
      const updatedList = items.map((item) =>
        item.id === id ? { ...item, isActive: true } : item,
      );

      dispatch(setCustomers(updatedList));
      toast.success(
        i18n.t("customers:sliceMessages.statusSuccess", {
          defaultValue: "Customer Activated",
        }),
      );
    } catch (error) {
      toast.error(
        i18n.t("customers:sliceMessages.statusError", {
          defaultValue: "Activation failed",
        }),
      );
    }
  };
};

export const handleDeactivate = (id: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await customersApi.deActivateCustomer(id);
      const { items } = getState().customers;
      const updatedList = items.map((item) =>
        item.id === id ? { ...item, isActive: false } : item,
      );

      dispatch(setCustomers(updatedList));
      toast.success(
        i18n.t("customers:sliceMessages.statusSuccess", {
          defaultValue: "Customer Deactivated",
        }),
      );
    } catch (error) {
      toast.error(
        i18n.t("customers:sliceMessages.statusError", {
          defaultValue: "Deactivation failed",
        }),
      );
    }
  };
};

export const uploadCustomerMedias = (customerId: string, files: File[]) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!files?.length) return;

      const state = getState();
      const userId = (await getCurrentUser()).userId;

      const { payloads, links } = await uploadMediaFiles(
        customersApi,
        customerId,
        files,
      );

      const newEntries = createResolvedMedia(
        payloads,
        links,
        customerId,
        userId,
      );

      const updatedItems = state.customers.items.map((item: Customer) =>
        item.id === customerId
          ? {
              ...item,
              medias: [...(item.medias || []), ...newEntries],
              mediaLinks: null,
            }
          : item,
      );

      dispatch(setCustomers(updatedItems));

      toast.success(
        i18n.t("customers:sliceMessages.uploadSuccess", {
          count: files.length,
          defaultValue: `${files.length} images added to gallery`,
        }),
      );
    } catch (err) {
      console.error("Customer Upload Error:", err);

      toast.error(
        i18n.t("customers:sliceMessages.uploadError", {
          defaultValue: "Failed to upload images",
        }),
      );

      throw err;
    }
  };
};

export const renameCustomerMedia = (
  customerId: string,
  fileId: string,
  newName: string,
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const state = getState();

      const currentCustomer = state.customers.items.find(
        (c: Customer) => c.id === customerId,
      );

      const targetFile = currentCustomer?.medias?.find(
        (m: any) => m.id === fileId,
      );

      if (!targetFile) {
        toast.error(i18n.t("slices.shared.fileNotFound", { ns: "common" }));
        return;
      }

      const payload = buildRenamePayload(targetFile, customerId, newName);

      await customersApi.uploadMedia(customerId, payload);

      const updatedItems = state.customers.items.map((item: Customer) =>
        item.id === customerId
          ? {
              ...item,
              medias: applyRename(item.medias || [], fileId, newName),
            }
          : item,
      );

      dispatch(setCustomers(updatedItems));

      toast.success(
        i18n.t("customers:sliceMessages.renameSuccess", {
          defaultValue: "File renamed successfully",
        }),
      );
    } catch (err) {
      console.error("Customer Rename Error:", err);

      toast.error(
        i18n.t("customers:sliceMessages.renameError", {
          defaultValue: "Failed to rename file",
        }),
      );
    }
  };
};

export const removeCustomerMedia = (
  customerId: string,
  filesToRemove: any[],
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!filesToRemove?.length) return;

      const state = getState();

      const currentCustomer = state.customers.items.find(
        (c: Customer) => c.id === customerId,
      );

      if (!currentCustomer?.medias) return;

      const idsToRemove = filesToRemove.map((f: any) => f.id);

      const payload = buildDeletePayload(
        currentCustomer.medias,
        customerId,
        idsToRemove,
      );
      // Nothing to remove that this record still lists.
      if (payload.length === 0) return;

      await customersApi.uploadMedia(customerId, payload);

      const updatedItems = state.customers.items.map((item: Customer) =>
        item.id === customerId
          ? {
              ...item,
              medias: applyDelete(item.medias || [], idsToRemove),
            }
          : item,
      );

      dispatch(setCustomers(updatedItems));

      toast.success(
        i18n.t("customers:sliceMessages.deleteImageSuccess", {
          defaultValue: "File removed successfully",
        }),
      );
    } catch (err) {
      console.error("Customer Remove Error:", err);

      toast.error(
        i18n.t("customers:sliceMessages.deleteImageError", {
          defaultValue: "Failed to remove file",
        }),
      );
    }
  };
};

interface CustomerState {
  items: Customer[];
  isLoading: boolean;
  pageIndex: number;
  hasMore: boolean;
  totalCustomers: number;
}

const initialState: CustomerState = {
  items: [],
  isLoading: false,
  pageIndex: 0,
  hasMore: true,
  totalCustomers: 0,
};

const customersSlice = createSlice({
  name: "customers",
  initialState,
  reducers: {
    setCustomers: (state, action) => {
      state.items = action.payload;
    },
    appendCustomers: (state, action) => {
      state.items = [...state.items, ...action.payload];
    },
    setPageIndex: (state, action) => {
      state.pageIndex = action.payload;
    },
    setHasMore: (state, action) => {
      state.hasMore = action.payload;
    },
    setTotalCustomers: (state, action) => {
      state.totalCustomers = action.payload;
    },
    resetCustomersList: (state) => {
      state.items = [];
      state.pageIndex = 0;
      state.hasMore = true;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
  },
});

export const {
  setCustomers,
  appendCustomers,
  setPageIndex,
  setHasMore,
  resetCustomersList,
  setLoading,
  setTotalCustomers,
} = customersSlice.actions;
export default customersSlice.reducer;
